import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import Table from '@app/src/components/Table'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import useBaselineAssessmentTemplate from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Hooks/useBaselineAssessmentTemplate'
import StandardReportHeader from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/StandardReportHeader'
import StandardReportsRow from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/StandardReportsRow'
import TemplateTypeCard from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/TemplateTypeCard'
import {
  createTemplateTypeAvatar,
  getTemplateTypeConfig,
  TemplateTypeId,
} from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/templateTypeConfig'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { ScrapingType } from '@app/src/types/resourceExplorer'
import { Chip, Stack } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

enum QuestionnaireFilterType {
  All = 'all',
  AIScreenings = 'aiScreenings',
  Assessments = 'assessments',
  Requests = 'requests',
}

const StandardQuestionnaireCollection: React.FC = () => {
  const { formatMessage } = useIntl()
  const [selectedFilter, setSelectedFilter] = useState<QuestionnaireFilterType>(QuestionnaireFilterType.All)
  const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery(
    {
      page: 1,
      pageSize: 50,
    },
    [selectedFilter],
  )

  const { baselineAssessmentTemplate } = useBaselineAssessmentTemplate()
  const baselineQuestionnaireId = baselineAssessmentTemplate?.questionnaireTemplateId

  const { items: baselineQuestionnaires = [] } = useFetchCollectionWithPost<QuestionnaireTemplate>({
    endpoint: endpoints.worldfavorTemplates,
    key: [FetchKey.RequestTemplate, 'baseline'],
    payload: {
      filter: baselineQuestionnaireId
        ? [
            {
              name: 'id',
              filters: [{ value: baselineQuestionnaireId, operator: Operators.EqualTo }],
            },
          ]
        : [],
      include: [
        'sections.questions.reportFlagRules',
        'assessmentQuestionnaireLink.assessmentTemplate.solutionAvailability',
      ],
      pagination: { pageNumber: 1, itemsPerPage: 1 },
    },
    options: { enabled: !!baselineQuestionnaireId },
  })

  const baselineQuestionnaire = baselineQuestionnaires[0]

  const filters = useMemo((): FilterGroup[] => {
    switch (selectedFilter) {
      case QuestionnaireFilterType.AIScreenings:
        return [
          {
            name: 'scrapingType',
            filters: [{ value: ScrapingType.AdverseScraping, operator: Operators.EqualTo }],
          },
        ]
      case QuestionnaireFilterType.Assessments:
        return [
          {
            name: 'assessmentQuestionnaireLink.id',
            filters: [{ value: 0, operator: Operators.GreaterThan }],
          },
        ]
      case QuestionnaireFilterType.Requests:
        return [
          {
            name: 'scrapingType',
            filters: [{ value: ScrapingType.AdverseScraping, operator: Operators.NotEqualTo }],
          },
        ]
      case QuestionnaireFilterType.All:
      default:
        return []
    }
  }, [selectedFilter, baselineQuestionnaireId])

  const {
    items: reports = [],
    count,
    isLoading,
    isError,
  } = useFetchCollectionWithPost<QuestionnaireTemplate>({
    endpoint: endpoints.worldfavorTemplates,
    key: FetchKey.RequestTemplate,
    payload: {
      filter: filters,
      include: [
        'sections.questions.reportFlagRules',
        'assessmentQuestionnaireLink.assessmentTemplate.solutionAvailability',
      ],
      sort: { target: 'title', order: SortOrder.ASCENDING },
      pagination: {
        pageNumber: page,
        itemsPerPage: pageSize,
      },
    },
  })

  const displayReports = useMemo(() => {
    if (selectedFilter === QuestionnaireFilterType.AIScreenings && baselineQuestionnaire) {
      const hasBaseline = reports.some(r => r.id === baselineQuestionnaireId)
      if (hasBaseline) {
        return reports
      }
      const combined = [baselineQuestionnaire, ...reports]
      return combined.sort((a, b) => a.title.localeCompare(b.title))
    }
    return reports
  }, [reports, baselineQuestionnaire, selectedFilter, baselineQuestionnaireId])

  const displayCount = useMemo(() => {
    if (selectedFilter === QuestionnaireFilterType.AIScreenings && baselineQuestionnaire) {
      const hasBaseline = reports.some(r => r.id === baselineQuestionnaireId)
      return hasBaseline ? count : count + 1
    }
    return count
  }, [count, selectedFilter, reports, baselineQuestionnaireId, baselineQuestionnaire])

  const filterChips = [
    { type: QuestionnaireFilterType.All, label: formatMessage({ id: 'questionnaireConfig.filters.all' }) },
    {
      type: QuestionnaireFilterType.AIScreenings,
      label: formatMessage({ id: 'questionnaireConfig.filters.aiScreenings' }),
    },
    {
      type: QuestionnaireFilterType.Requests,
      label: formatMessage({ id: 'questionnaireConfig.filters.questionnaires' }),
    },
    {
      type: QuestionnaireFilterType.Assessments,
      label: formatMessage({ id: 'questionnaireConfig.filters.assessments' }),
    },
  ]

  const templateTypeConfigs = [
    getTemplateTypeConfig(TemplateTypeId.AiScreening),
    getTemplateTypeConfig(TemplateTypeId.Questionnaire),
    { ...getTemplateTypeConfig(TemplateTypeId.Assessment), sidetitleId: 'questionnaireConfig.reuseAnswers' },
  ]

  return (
    <>
      <Stack bgcolor="grey.50" py={2} pl={3} pr={3} ml={-4} spacing={2}>
        <Stack direction="row" spacing={2}>
          {templateTypeConfigs.map(config => (
            <TemplateTypeCard
              key={config.id}
              avatar={createTemplateTypeAvatar(config)}
              titleId={config.titleId}
              sidetitleId={config.sidetitleId}
              descriptionId={config.descriptionId}
            />
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {filterChips.map(chip => (
            <Chip
              key={chip.type}
              label={chip.label}
              onClick={() => setSelectedFilter(chip.type)}
              sx={
                selectedFilter === chip.type
                  ? undefined
                  : {
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                    }
              }
            />
          ))}
        </Stack>
      </Stack>
      <Table<QuestionnaireTemplate>
        RowComponent={StandardReportsRow}
        HeaderComponent={StandardReportHeader}
        data={displayReports}
        page={page}
        isLoading={isLoading}
        count={displayCount}
        isError={isError}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  )
}

export default StandardQuestionnaireCollection
